import ScrollToTop from '@/features/routes/ScrollToTop';
import { trackingInit } from '@/helpers/trackingHelper';
import 'dayjs/locale/sv';
import Highcharts from 'highcharts';
import { debounce } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import { AdapterDayjs, LocalizationProvider, useMediaQuery } from 'ui/mui';
import ThemeProvider from 'ui/theme/ThemeProvider';
import { getSelectedCustomer } from './app/slices/userSlice';
import userManager from './app/userManager';
import { verifyNetworkConnection } from '@/hooks/useNetworkStatus';
import ErrorWrapper from './ErrorWrapper';
import { addToWindowObject } from './helpers/utils';
import Layout from './layout/Layout';
import Page from './layout/page/Page';
import AddCompanyUser from './pages/_company-pages/add-company-user/AddCompanyUser';
import Callback from './pages/callback/Callback';
import Download from './pages/download/Download';
import NotFound from './pages/not-found/NotFound';
import Signout from './pages/signout/Signout';
import SwitchUser from './pages/switch-user/SwitchUser';
import { State } from './types/State';
import { DebugProvider } from './pages/debug/contexts/DebugContext';
import { notFoundRoute } from './features/routes/GetRoutes';

addToWindowObject('Highcharts', Highcharts);

const App = () => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [darkMode, setDarkMode] = useState(prefersDarkMode);
  const { selectedProperty } = useSelector((state: State) => state.user);
  const { user, isLoadingUser } = useSelector((state: State) => state.auth);
  const selectedCustomer = useSelector((state: State) =>
    getSelectedCustomer(state),
  );

  const signinFunc = debounce(
    () => {
      localStorage.setItem(
        'redirectUri',
        window.location.pathname + window.location.search,
      );
      userManager.signinRedirect();
    },
    500,
    { leading: true, trailing: true },
  );

  useEffect(() => {
    setDarkMode(prefersDarkMode);
  }, [prefersDarkMode]);

  useEffect(() => {
    if (
      !isLoadingUser &&
      (!user || !selectedCustomer) &&
      window.location.pathname !== '/callback' &&
      window.location.pathname !== '/signout' &&
      window.location.pathname !== '/silent-renew' &&
      window.location.pathname !== '/foretag/skapa-anvandare' &&
      !window.location.pathname.includes('/ladda-ner')
    ) {
      signinFunc();
    }
  }, [isLoadingUser, user, selectedCustomer, signinFunc]);

  const appReady = !!selectedCustomer && !!selectedProperty && !!user;

  const trackingInitialized = useRef(false);
  useEffect(() => {
    if (selectedCustomer && !trackingInitialized.current) {
      trackingInitialized.current = true;
      trackingInit(selectedCustomer);
    }
  }, [selectedCustomer]);

  return (
    <ThemeProvider darkMode={darkMode}>
      <ErrorWrapper>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='sv'>
          <DebugProvider darkMode={darkMode} setDarkMode={setDarkMode}>
            <ScrollToTop />
            <Routes>
              <Route path='/ladda-ner/:token' element={<Download />} />
              <Route path='/callback' element={<Callback />} />
              <Route
                path='/switch-user/:customerAccountId?'
                element={<SwitchUser />}
              />
              <Route path='/signout' element={<Signout />} />
              <Route
                path='/foretag/skapa-anvandare'
                element={<AddCompanyUser />}
              />
              {appReady && <Route path='/*' element={<Layout />} />}
              <Route
                path='*'
                element={
                  <Page route={notFoundRoute}>
                    <NotFound />
                  </Page>
                }
              />
            </Routes>
          </DebugProvider>
        </LocalizationProvider>
      </ErrorWrapper>
    </ThemeProvider>
  );
};

export default App;
